import type { ReactNode } from "react";

import { ButtonLink } from "~/components/ui/button";
import { CDNImage } from "~/components/ui/cdn-image";
import { WatchIntroDialog } from "~/components/ui/video-embed";
import { shouldShowCoachCTAs } from "~/lib/coach-cta-utils";

import { CoachAtAGlance } from "./at-a-glance";

export type CoachHeroData = {
  firstName: string;
  lastName: string;
  fullName: string;
  profilePictureUrl: string | null;
  hasBookingCalendar?: boolean;
  profile: {
    coachingTitle: string | null;
    title: string | null;
    ctaButtonText: string | null;
    contactMode: "booking" | "messaging" | null;
    isPreGeneratedProfile: boolean;
    mainImage: string | null;
    mainVideo: string | null;
    audienceFor: string | null;
    formOfConduct: string | null;
    coachingStyle: string | null;
    typicalEngagement: string | null;
    brandName: string | null;
    brandLogoUrl: string | null;
  };
  username: string;
};

type Props = {
  coach: CoachHeroData;
  matchScoreSlot?: ReactNode;
  badgeSlot?: ReactNode;
};

export function CoachHero({ coach, matchScoreSlot, badgeSlot }: Props) {
  const { profile } = coach;
  // Pre-generated (unclaimed) profiles must not surface contact/booking CTAs;
  // their only CTA is the claim banner rendered above the hero.
  const showCta = shouldShowCoachCTAs(coach);
  const ctaHref = ctaPath(coach.username, profile.contactMode);
  const ctaLabel = profile.ctaButtonText?.trim() || "Start a conversation";
  const kicker = profile.coachingTitle?.trim() || "Coach";

  return (
    <section className="relative overflow-hidden border-b border-gray-200 bg-linear-to-b from-purple-50/60 via-white to-white">
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 right-0 size-[640px] -translate-x-1/4 rounded-full bg-purple-200/40 blur-[120px]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 -left-32 size-[480px] rounded-full bg-violet-200/30 blur-[100px]"
      />

      <div className="relative z-10 mx-auto max-w-7xl px-4 pt-6 pb-14 md:pt-10 md:pb-20 xl:px-0">
        <div className="grid gap-10 md:grid-cols-[1fr_400px] md:items-end md:gap-16 lg:grid-cols-[1fr_440px]">
          <div>
            {matchScoreSlot}
            {badgeSlot}
            <h1
              aria-label={coach.fullName}
              className="font-heading text-4xl leading-[1.05] font-medium tracking-tight text-gray-900 md:text-5xl md:leading-none lg:text-6xl xl:text-7xl"
            >
              <span>{coach.firstName}</span>{" "}
              <span className="md:block">{coach.lastName}</span>
            </h1>
            <p className="mt-4 font-medium text-purple-700 md:text-lg">
              {kicker}
            </p>
            {profile.title?.trim() && (
              <p className="mt-5 max-w-2xl text-xl leading-snug text-gray-700 md:text-2xl">
                {profile.title}
              </p>
            )}
            {(showCta || profile.mainVideo) && (
              <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-3">
                {showCta && (
                  <ButtonLink variant="fancy" size="lg" href={ctaHref}>
                    {ctaLabel}
                  </ButtonLink>
                )}
                {profile.mainVideo && (
                  <WatchIntroDialog
                    videoUrl={profile.mainVideo}
                    fullName={coach.fullName}
                  />
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col">
            {(() => {
              const heroImageUrl =
                profile.mainImage?.trim() || coach.profilePictureUrl;
              return heroImageUrl ? (
                <CDNImage
                  src={heroImageUrl}
                  alt={`Portrait of ${coach.fullName}`}
                  width={880}
                  height={1100}
                  priority
                  className="aspect-4/5 w-full rounded-2xl object-cover shadow-xl ring-1 shadow-purple-900/10 ring-gray-900/5"
                />
              ) : (
                <div
                  aria-hidden
                  className="aspect-4/5 w-full rounded-2xl bg-gray-50 ring-1 ring-gray-900/5"
                />
              );
            })()}

            <CoachAtAGlance
              audienceFor={profile.audienceFor}
              formOfConduct={profile.formOfConduct}
              coachingStyle={profile.coachingStyle}
              typicalEngagement={profile.typicalEngagement}
              brandName={profile.brandName}
              brandLogoUrl={profile.brandLogoUrl}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function ctaPath(
  username: string,
  contactMode: "booking" | "messaging" | null,
): string {
  return contactMode === "booking"
    ? `/${username}/book`
    : `/${username}/contact`;
}
